// Copied from flowsjs `packages/ui/theme/src/tokens.ts` (`@aiuirun/ui-theme`), which is not published.
// Keep in step with the source; divergence should be deliberate and noted here.

// Design tokens
// Fonts: the spec calls for the proprietary "Styrene B"; we substitute "Hanken Grotesk" (+ "JetBrains
// Mono" for the mono role).

export interface DesignTokens {
  surface: { bg: string; card: string; cardSunken: string; railBg: string };
  chrome: {
    base: string;
    raised: string;
    line: string;
    ink: string;
    inkHi: string;
  };
  ink: { primary: string; secondary: string; muted: string; faint: string };
  line: { default: string; faint: string; strong: string };
  accent: { base: string; hover: string; soft: string; ink: string };
  /** Translucent state layers — washed over any surface (context-independent) for hover/active.
   *  `veil` marks what is authored but not presented: lighter than hover, so it never reads as one. */
  state: { hover: string; active: string; hoverDanger: string; veil: string };
  semantic: {
    success: string;
    successSoft: string;
    successInk: string;
    warning: string;
    warningSoft: string;
    warningInk: string;
    danger: string;
    dangerSoft: string;
    dangerInk: string;
    info: string;
    infoSoft: string;
    infoInk: string;
  };
  /**
   * The document viewer's surround — the table a document lies on.
   *
   * ONE tone behind a page and around a workbook's cells, so the pane does not change colour when the
   * document does. It is the SUNKEN well of a light card, not a dark lightbox: a pane holding a file
   * list, a toolbar and grid furniture is UI, and at half the window a near-black mass reads as a
   * different application. `well` is the single cut-in on it (a grid's corner); `glyph` is what is
   * written on the furniture — the letters and numbers that address the sheet.
   */
  viewer: { surround: string; well: string; glyph: string };
  radius: { sm: number; md: number; lg: number; xl: number; pill: number };
  /**
   * Fixed dimensions two components must agree on without knowing about each other.
   *
   * `bar` is a titling bar — a page header, a rail's head, a chat's head. The console, the chat and
   * the flexcell editor each draw one and none imports another, so a shared number is the only thing
   * that can keep them on one line; a comment saying "matches the other one" cannot. It holds a 34px
   * control with room around it.
   *
   * `gutter` is the left inset everything down the chrome's left edge shares: the app's mark, a rail's
   * eyebrow, the medallion on a rail row. They sit in different components and stack vertically, which
   * is exactly where a few pixels of disagreement read as a mistake.
   *
   * `mark` is the app's mark's size, so what lines up under it finds its centre: `gutter + mark / 2`.
   */
  size: { bar: number; gutter: number; mark: number };
  /** The one face carries hierarchy by weight, so these are named rather than spelled 400/500/600. */
  weight: { regular: number; medium: number; semibold: number; bold: number };
  shadow: { 1: string; 2: string; 3: string; 4: string; overlay: string };
  font: { sans: string; mono: string };
}

export const lightTokens: DesignTokens = {
  surface: {
    bg: "#F7F7F5",
    card: "#FFFFFF",
    cardSunken: "#F3EFE6",
    railBg: "#F5F1E8",
  },
  chrome: {
    base: "#1F1E1C",
    raised: "#2A2926",
    line: "#35332E",
    ink: "#B8B3A6",
    inkHi: "#FAF7F2",
  },
  ink: {
    primary: "#1F1E1C",
    secondary: "#3D3A34",
    muted: "#6B6659",
    faint: "#9A9384",
  },
  line: { default: "#E4DDD0", faint: "#EEE8DB", strong: "#D4CBB8" },
  accent: {
    base: "#D97757",
    hover: "#C96543",
    soft: "#F4E4D9",
    ink: "#7A3A1F",
  },
  state: {
    hover: "rgba(31,30,28,0.06)", // ink @ 6%
    active: "rgba(31,30,28,0.10)",
    hoverDanger: "rgba(166,75,58,0.12)", // danger @ 12%
    veil: "rgba(31,30,28,0.035)", // ink @ 3.5%
  },
  semantic: {
    success: "#5B7A4E",
    successSoft: "#E3EADB",
    successInk: "#3C5331",
    warning: "#B8803A",
    warningSoft: "#F4E8D0",
    warningInk: "#7A521E",
    danger: "#A64B3A",
    dangerSoft: "#F1DDD6",
    dangerInk: "#7C3325",
    info: "#4A6783",
    infoSoft: "#DFE5EC",
    infoInk: "#324A63",
  },
  viewer: { surround: "#F3EFE6", well: "#E4DDD0", glyph: "#9A9384" },
  radius: { sm: 4, md: 6, lg: 8, xl: 14, pill: 100 },
  size: { bar: 52, gutter: 12, mark: 18 },
  weight: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  shadow: {
    1: "0 1px 2px 0 rgba(50,40,20,0.04)",
    2: "0 2px 6px 0 rgba(50,40,20,0.05)",
    3: "0 4px 14px 0 rgba(50,40,20,0.06)",
    4: "0 8px 28px 0 rgba(50,40,20,0.10)",
    overlay: "0 16px 40px 0 rgba(40,30,15,0.12)",
  },
  font: {
    sans: '"Hanken Grotesk", -apple-system, "Helvetica Neue", Arial, sans-serif',
    mono: '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, monospace',
  },
};

export const darkTokens: DesignTokens = {
  ...lightTokens,
  surface: {
    bg: "#1A1917",
    card: lightTokens.chrome.raised,
    cardSunken: "#201F1C",
    railBg: "#232220",
  },
  ink: {
    primary: lightTokens.chrome.inkHi,
    secondary: "#DAD5C9",
    muted: lightTokens.chrome.ink,
    faint: "#847E70",
  },
  line: {
    default: lightTokens.chrome.line,
    faint: "#2C2A26",
    strong: "#43403A",
  },
  state: {
    hover: "rgba(250,247,242,0.07)", // light ink @ 7% (a light wash on dark surfaces)
    active: "rgba(250,247,242,0.12)",
    hoverDanger: "rgba(221,148,132,0.18)",
    veil: "rgba(250,247,242,0.04)",
  },
  semantic: {
    ...lightTokens.semantic,
    successSoft: "rgba(91,122,78,0.24)",
    successInk: "#A9C49A",
    warningSoft: "rgba(184,128,58,0.24)",
    warningInk: "#DDB173",
    dangerSoft: "rgba(166,75,58,0.24)",
    dangerInk: "#DD9484",
    infoSoft: "rgba(74,103,131,0.26)",
    infoInk: "#9DB6D0",
  },
};
